import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import { ScholarshipCard } from "../../shared/component/Card/Card";
import * as RoutePath from "../../shared/utils/routeLink";
import * as Variable from "../../shared/utils/variables";
import * as ActionTypes from "../../store/actions/actionTypes";
import ListingComponent from "../../shared/component/ListingComponent/ListingComponent";
import FilterBox from "../../shared/component/FilterBox/FilterBox";
import NoContentComponent from "../../shared/component/NoContentComponent/NoContentComponent";
import { toast } from "react-toastify";
import { SearchIcon } from "../../shared/component/svg/Icons";
import { formatString } from "../../shared/utils/string";
import useDebounce from "../../shared/hooks/debounce";
import makeRequest from "../../shared/utils/request";
import { generateRequestOptions } from "../../shared/utils/apiEndPoints";
import { ScholarshipListCard } from "../../shared/component/ListItem/ListItem";

const ITEMS_PER_PAGE = 60;

const SCHOLARSHIP_GIVER_FILTER = [
	{ id: "government", name: Variable.GOVERNMENT },
	{ id: "college", name: Variable.COLLEGE },
	{ id: "organization", name: Variable.ORGANIZATION },
	{ id: "international", name: Variable.INTERNATIONAL },
];
const DESTINATION_FILTER = [
	{ id: "domestic", name: Variable.DOMESTIC },
	{ id: "abroad", name: Variable.INTERNATIONAL_DESTINATION },
];

const ScholarshipListing = ({ ENV_NAME, isAuthenticated, fetchingAddRemoveFavorites, ...props }) => {
	const [scholarship, setScholarships] = useState(null);
	const [isLoading, setIsLoading] = useState(null);
	const [filters, setFilters] = useState({
		giver: "allGiver",
		destination: "allDestination",
	});
	const [searchTemp, setSearchTemp] = useState("");
	const debounceSearch = useDebounce(searchTemp, 750);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState({
		has_next: false,
		total_items: 0,
	});
	const [view, setView] = useState("big");

	useEffect(() => {
		if (searchTemp !== filters?.search && (searchTemp.length >= 3 || searchTemp === "")) {
			setFilters((p) => ({ ...p, search: debounceSearch }));
		}
	}, [debounceSearch]); // eslint-disable-line

	useEffect(() => {
		setPage(1);
		getScholarship(1);
	}, [filters]); // eslint-disable-line

	useEffect(() => {
		if (page > 1) getScholarship(page);
	}, [page]); // eslint-disable-line

	const getScholarship = async (page) => {
		if (isLoading) return;
		setIsLoading(true);

		// Store the current value, and add the loading card
		// While the API is calling
		let currScholarships = page === 1 ? [] : scholarship || [];
		setScholarships([
			...currScholarships,
			...[...Array(ITEMS_PER_PAGE)].map((i) => ({
				isLoading: true,
			})),
		]);

		// Filters checking
		let scholarship_giver = filters?.giver === "allGiver" ? undefined : filters?.giver;
		let campus_destination = filters?.destination === "allDestination" ? undefined : filters?.destination;

		// Call the API
		let res = await makeRequest(
			generateRequestOptions("scholarshipList", {
				queryParams: {
					page,
					page_size: ITEMS_PER_PAGE,
					search: filters?.search || undefined,
					scholarship_giver,
					campus_destination,
				},
			})
		);
		if (res.code === 200) {
			setScholarships([...currScholarships, ...res.data]);
			setTotal({ has_next: res.next, total_items: res.total_items });
		} else {
			setScholarships(currScholarships);
			setTotal((p) => ({ ...p, has_next: false }));
			toast.error(res.message || Variable.SOMETHING_WENT_WRONG_TRY_AGAIN[ENV_NAME]);
		}

		setIsLoading(false);
	};

	const onChangeField = (e, parent, radio = false) => {
		if (radio) {
			setFilters((p) => ({ ...p, [parent]: e.target.id }));
		} else {
			setFilters((p) => ({
				...p,
				[parent]: {
					...p?.[parent],
					...(p?.[parent]?.[parent + "All"] && !e.target.checked ? { [parent + "All"]: false } : {}),
					[e.target.name]: e.target.checked,
				},
			}));
		}
	};

	return (
		<ListingComponent
			className="career-listing"
			hasView={true}
			view={view}
			setView={setView}
			filters={
				<>
					<FilterBox>
						<div className="form-group search-bar mb-0">
							<SearchIcon className="searchIcon" />
							<input
								type="text"
								name="searchText"
								value={searchTemp || ""}
								onChange={(e) => setSearchTemp(e.target.value)}
								placeholder={formatString(
									Variable.SRC_BY_PLACEHOLDER[ENV_NAME],
									Variable.SCHOLARSHIP[ENV_NAME]
								)}
							/>
						</div>
					</FilterBox>
					<FilterBox
						header={Variable.SCHOLARSHIP_GIVER[ENV_NAME]}
						collapsible={1}
						eventkey="0"
						opendefault={1}
					>
						<div className="form-group form-check">
							<input
								type="radio"
								className="form-check-input"
								id="allGiver"
								name="giver"
								checked={filters?.giver === "allGiver" || false}
								onChange={(e) => onChangeField(e, "giver", true)}
								disabled={isLoading}
							/>
							<label className="form-check-label" htmlFor="allGiver">
								{Variable.ALL_SCHOLARSHIP_GIVER[ENV_NAME]}
							</label>
						</div>
						{SCHOLARSHIP_GIVER_FILTER?.map((i) => (
							<div className="form-group form-check" key={i?.id}>
								<input
									type="radio"
									className="form-check-input"
									id={`${i?.id}`}
									name="giver"
									checked={filters?.giver === `${i?.id}` || false}
									onChange={(e) => onChangeField(e, "giver", true)}
									disabled={isLoading}
								/>
								<label className="form-check-label" htmlFor={`${i?.id}`}>
									{i?.name?.[ENV_NAME]}
								</label>
							</div>
						))}
					</FilterBox>
					<FilterBox header={Variable.DESTINATION[ENV_NAME]} collapsible={1} eventkey="0" opendefault={1}>
						<div className="form-group form-check">
							<input
								type="radio"
								className="form-check-input"
								id="allDestination"
								name="destination"
								checked={filters?.destination === "allDestination" || false}
								onChange={(e) => onChangeField(e, "destination", true)}
								disabled={isLoading}
							/>
							<label className="form-check-label" htmlFor="allDestination">
								{Variable.ALL_DESTINATION[ENV_NAME]}
							</label>
						</div>
						{DESTINATION_FILTER?.map((i) => (
							<div className="form-group form-check" key={i?.id}>
								<input
									type="radio"
									className="form-check-input"
									id={`${i?.id}`}
									name="destination"
									checked={filters?.destination === `${i?.id}` || false}
									onChange={(e) => onChangeField(e, "destination", true)}
									disabled={isLoading}
								/>
								<label className="form-check-label" htmlFor={`${i?.id}`}>
									{i?.name?.[ENV_NAME]}
								</label>
							</div>
						))}
					</FilterBox>
				</>
			}
			contents={
				!isLoading && scholarship?.length === 0 ? (
					<NoContentComponent className="my-xl" description=" " />
				) : (
					(scholarship || [])?.map((i, idx) => (
						<Link
							key={i?.id || idx}
							to={i?.id ? RoutePath.SCHOLARSHIP_DETAIL + "/" + i?.id : ""}
							className="text-decoration-none text-label-dark-gray-2"
						>
							{view !== "list" ? (
								<ScholarshipCard
									key={i?.id || idx}
									isLoading={i?.isLoading}
									data={i?.isLoading ? undefined : i}
									onClickFavorite={() => {
										if (i?.isLoading || fetchingAddRemoveFavorites) return;
										if (isAuthenticated) {
											props.addRemoveFavorite(
												i?.is_favourite ? "remove" : "add",
												"scholarship",
												i?.is_favourite ? i?.fav_obj_id : i?.id,
												(res) => {
													setScholarships((prev) =>
														prev.map((p) => {
															if (p?.id !== i?.id) return p;
															return {
																...p,
																is_favourite: !i?.is_favourite,
																fav_obj_id: i?.is_favourite ? null : res?.data?.id,
															};
														})
													);
												}
											);
										} else {
											props.openLoginModal();
										}
									}}
									onClick={() => {}}
									ENV_NAME={ENV_NAME}
									smallcard={view === "small"}
								/>
							) : (
								<ScholarshipListCard
									key={i?.id || idx}
									isLoading={i?.isLoading}
									data={i?.isLoading ? undefined : i}
									onClickFavorite={() => {
										if (i?.isLoading || fetchingAddRemoveFavorites) return;
										if (isAuthenticated) {
											props.addRemoveFavorite(
												i?.is_favourite ? "remove" : "add",
												"scholarship",
												i?.is_favourite ? i?.fav_obj_id : i?.id,
												(res) => {
													setScholarships((prev) =>
														prev.map((p) => {
															if (p?.id !== i?.id) return p;
															return {
																...p,
																is_favourite: !i?.is_favourite,
																fav_obj_id: i?.is_favourite ? null : res?.data?.id,
															};
														})
													);
												}
											);
										} else {
											props.openLoginModal();
										}
									}}
									onClick={() => {}}
									ENV_NAME={ENV_NAME}
								/>
							)}
						</Link>
					))
				)
			}
			contentsClassName={!isLoading && scholarship?.length === 0 ? "no-grid" : undefined}
			showingLabel={`${total?.total_items || 0} ${Variable.SCHOLARSHIP[ENV_NAME]}`}
			showMoreOnClick={() => !isLoading && setPage((p) => p + 1)}
			hasMore={!isLoading ? total.has_next : false}
		/>
	);
};

const mapStateToProps = (state) => {
	return {
		isAuthenticated: state.auth.isAuthenticated,
		ENV_NAME: state.auth.selectedEnvironment || "bhs",
		fetchingAddRemoveFavorites: state.auth.fetchingAddRemoveFavorites,
	};
};

const mapStateToDispatch = (dispatch) => {
	return {
		openLoginModal: () =>
			dispatch({
				type: ActionTypes.OPEN_LOGIN_MODAL,
			}),
		addRemoveFavorite: (action, type, id, onSuccess = null, onFail = null) =>
			dispatch({
				type: ActionTypes.ADD_REMOVE_FAVORITE,
				data: { action, type, id },
				onSuccess,
				onFail,
			}),
	};
};

export default connect(mapStateToProps, mapStateToDispatch)(withRouter(ScholarshipListing));
